/**
 * MealsHowToTour — one-time onboarding for the /meals page.
 *
 * Shows three friendly callouts the first time a user lands on /meals:
 *   1. Tap any photo for the recipe + video + ingredients.
 *   2. Click the 📖 icon for deeper details.
 *   3. Share with Cook button sends a no-login mobile link.
 *
 * Persists "seen" state in localStorage under MealsHowToTour.v1.
 * Bump the version constant after a major feature drop to re-prompt users.
 */
import React, { useEffect, useState } from 'react';
import { X, ArrowRight, ArrowLeft, ForkKnife, BookOpen, WhatsappLogo } from '@phosphor-icons/react';

const STORAGE_KEY = 'MealsHowToTour.v1.seen';

const STEPS = [
  {
    icon: ForkKnife,
    title: 'Tap any dish photo',
    body: 'Click a recipe card or a planner chip\u2019s photo to open the full recipe \u2014 description, video, ingredients, varieties, and step-by-step method.',
    accent: '#FF6B35',
  },
  {
    icon: BookOpen,
    title: 'Want even more detail?',
    body: 'Click the small \uD83D\uDCD6 (book) icon on any meal chip for the same rich detail view. Inside, you can also \u201CGenerate alternative recipe\u201D if the family wants a different take.',
    accent: '#A855F7',
  },
  {
    icon: WhatsappLogo,
    title: 'Share with your cook',
    body: 'Tap the green WhatsApp button inside any recipe to send a no-login mobile-friendly page \u2014 with ingredients, video and step photos in Hindi or English. He doesn\u2019t need the app.',
    accent: '#25D366',
  },
];

export default function MealsHowToTour() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== '1'; } catch { return false; }
  });
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;
  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4" data-testid="meals-how-to-tour">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative w-full max-w-md rounded-2xl bg-neutral-900 border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all ${i === step ? 'w-6 bg-white' : 'w-1.5 bg-white/30'}`} />
            ))}
          </div>
          <button onClick={dismiss} data-testid="meals-how-to-close" className="p-1 rounded text-neutral-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-3">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: `${s.accent}22`, border: `1px solid ${s.accent}55` }}>
            <Icon size={28} weight="duotone" style={{ color: s.accent }} />
          </div>
          <h3 className="text-xl font-bold text-white" data-testid="meals-how-to-title">{s.title}</h3>
          <p className="text-sm text-neutral-300 leading-relaxed">{s.body}</p>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/5 flex items-center justify-between gap-2">
          <button onClick={dismiss} data-testid="meals-how-to-skip"
            className="text-xs text-neutral-500 hover:text-neutral-300 font-semibold">
            Skip tour
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} data-testid="meals-how-to-back"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-neutral-300 text-xs font-semibold">
                <ArrowLeft size={14} /> Back
              </button>
            )}
            <button onClick={() => isLast ? dismiss() : setStep(step + 1)} data-testid="meals-how-to-next"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#FF6B35] hover:opacity-90 text-white text-xs font-bold">
              {isLast ? 'Got it' : 'Next'} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
